import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const SLIDES = [
  {
    img: "/images/d/d-5.webp",
    alt: "Cephe Kaplama",
    label: "Cephe Kaplama",
    position: "center -6%",
    zoom: 1.08,
  },
  {
    img: "/images/b.webp",
    alt: "Kutu Harf Tabela",
    label: "Kutu Harf Tabela",
    position: "center center",
  },
  {
    img: "/images/totemt/t.webp",
    alt: "Totem Tabela & Dekorasyon",
    label: "Totem Tabela",
    position: "center 40%",
  },
];

const STATS = [
  { value: "1620+", label: "Proje / Tabela" },
  { value: "300+", label: "Mutlu Marka" },
  { value: "20+", label: "Yıl Tecrübe" },
  { value: "18", label: "Şehirde Proje" },
];

const INTERVAL = 5000;

const HeroSection = () => {
  const [current, setCurrent] = useState(1);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCycle = (idx: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);

    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min((elapsed / INTERVAL) * 100, 100));
    }, 50);

    timerRef.current = setTimeout(() => {
      const next = (idx + 1) % SLIDES.length;
      setCurrent(next);
      startCycle(next);
    }, INTERVAL);
  };

  useEffect(() => {
    startCycle(current);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (idx: number) => {
    setCurrent(idx);
    startCycle(idx);
  };

  return (
    <section className="relative min-h-screen bg-[#0D0D0D] flex flex-col overflow-hidden">
      {/* Slides */}
      {SLIDES.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.img}
            alt={slide.alt}
            className="w-full h-full object-cover object-center"
            style={{
              objectPosition: slide.position,
              transform: `scale(${slide.zoom ?? 1})`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        </div>
      ))}

      {/* Left stripe */}
      <div className="absolute top-0 left-0 w-1.5 h-full bg-[#E6A800] z-20 anim-stripe" />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center min-h-screen max-w-7xl mx-auto px-8 md:px-16 lg:px-20 w-full pt-32 pb-20">
        <div className="flex items-center gap-3 mb-8 anim-left d0">
          <div className="w-8 h-[2px] bg-[#E6A800]" />
          <span className="font-head text-[#E6A800] tracking-[0.22em] text-xs font-700 uppercase">
            Osmaniye · Est. 2004
          </span>
        </div>

        <h1 className="font-head font-black uppercase leading-[0.88]">
          <span className="block text-white text-[clamp(60px,9.5vw,125px)] anim-up d1">
            Tabela
          </span>
          <span className="block text-white text-[clamp(60px,9.5vw,125px)] anim-up d2">
            &amp; Reklam
          </span>
          <span className="block text-[#E6A800] text-[clamp(60px,9.5vw,125px)] anim-up d3">
            Uzmanı
          </span>
        </h1>

        <p className="text-white/60 text-base leading-relaxed max-w-md mt-8 mb-10 anim-up d4">
          Cephe kaplama, kutu harf tabela, LED sistemleri ve dekorasyon
          hizmetleriyle markanızı öne çıkarıyoruz. 20 yıl tecrübe, anahtar
          teslim projeler.
        </p>

        <div className="flex flex-wrap gap-4 mb-14 anim-up d5">
          <Link
            to="/projeler"
            className="inline-flex items-center gap-2.5 px-7 py-4 bg-[#E6A800] text-white hover:bg-[#C99500] transition-all duration-300 font-head font-700 uppercase tracking-wider text-sm cursor-pointer whitespace-nowrap group"
          >
            Projeleri Gör
            <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 tr" />
          </Link>
          <a
            href="tel:+905511830918"
            className="inline-flex items-center gap-2.5 px-7 py-4 btn-outline-light font-head font-700 uppercase tracking-wider text-sm cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-sm" />
            +90 551 183 09 18
          </a>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-10 anim-up d6">
          {STATS.map((s) => (
            <div key={s.label} className="flex flex-col">
              <span className="font-head font-black text-white text-4xl leading-none">
                {s.value}
              </span>
              <span className="text-white/40 text-xs mt-1 uppercase tracking-widest font-head">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            className={`h-[3px] tr cursor-pointer ${
              i === current ? "w-8 bg-[#E6A800]" : "w-4 bg-white/30 hover:bg-white/60"
            }`}
            aria-label={`Slayt ${i + 1}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>

      {/* Side labels */}
      <div className="absolute top-1/2 right-8 -translate-y-1/2 z-20 hidden lg:flex flex-col gap-2">
        {SLIDES.map((slide, i) => (
          <button
            key={i}
            className={`flex items-center gap-2 cursor-pointer group tr ${
              i === current ? "opacity-100" : "opacity-30 hover:opacity-60"
            }`}
            aria-label={slide.label}
            onClick={() => goTo(i)}
          >
            <span
              className={`h-[1px] tr ${
                i === current ? "w-6 bg-[#E6A800]" : "w-3 bg-white"
              }`}
            />
            <span className="font-head text-white text-[10px] uppercase tracking-[0.18em]">
              {slide.label}
            </span>
          </button>
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 z-20 hidden md:flex flex-col items-center gap-1 anim-down d7">
        <span className="font-head text-white/30 text-[9px] uppercase tracking-[0.25em]">
          Kaydır
        </span>
        <div className="w-[1px] h-8 bg-white/20 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-[#E6A800]" />
        </div>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/5 z-20">
        <div
          className="h-full bg-[#E6A800] transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>
    </section>
  );
};

export default HeroSection;